"use client";

import { Select as SelectPrimitive } from "@base-ui/react/select";
import { IconCheck, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import type * as React from "react";
import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

/** Trigger look shared with select-like popovers (set `data-size` alongside). */
const selectTriggerClassName =
  "flex w-fit items-center justify-between gap-2 rounded-[var(--paper-radius-control)] border border-control-border bg-control-default pr-2.5 pl-3 text-sm whitespace-nowrap outline-none select-none ring-ring/15 transition-[border-color,background-color,box-shadow] duration-[var(--duration-fast)] hover:border-control-border-hover hover:bg-control-hover active:border-control-border-hover active:bg-control-pressed focus-visible:border-ring focus-visible:ring-2 disabled:cursor-not-allowed disabled:bg-muted/60 disabled:opacity-60 aria-expanded:border-control-border-hover aria-expanded:bg-control-highlight aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15 data-placeholder:text-muted-foreground data-[size=compact]:h-7 data-[size=compact]:gap-1.5 data-[size=compact]:px-2.5 data-[size=compact]:text-[11px] data-[size=compact]:font-medium data-[size=compact]:leading-[15px] data-[size=default]:h-9 data-[size=sm]:h-8 data-[size=sm]:text-xs data-[size=sm]:font-medium data-[size=sm]:leading-4 data-[size=lg]:h-10 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-1.5 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 data-[size=compact]:[&_svg:not([class*='size-'])]:size-3 data-[size=xs]:h-[22px] data-[size=xs]:gap-1 data-[size=xs]:rounded-xs data-[size=xs]:pr-1 data-[size=xs]:pl-1.5 data-[size=xs]:text-xs data-[size=xs]:font-medium data-[size=xs]:leading-4 data-[size=xs]:[&_[data-slot=select-icon]]:size-3 data-[variant=ghost]:border-transparent data-[variant=ghost]:bg-transparent data-[variant=ghost]:hover:border-transparent data-[variant=ghost]:hover:bg-state-hover data-[variant=ghost]:active:bg-state-hover data-[variant=ghost]:aria-expanded:border-transparent data-[variant=ghost]:aria-expanded:bg-state-hover data-[variant=ghost]:[&_[data-slot=select-icon]]:opacity-45 data-[variant=ghost]:[&_[data-slot=select-icon]]:transition-opacity data-[variant=ghost]:hover:[&_[data-slot=select-icon]]:opacity-100 data-[variant=ghost]:focus-visible:[&_[data-slot=select-icon]]:opacity-100 data-[variant=ghost]:aria-expanded:[&_[data-slot=select-icon]]:opacity-100";

function SelectGroup({ className, ...props }: SelectPrimitive.Group.Props) {
  return (
    <SelectPrimitive.Group
      data-slot="select-group"
      className={cn("scroll-my-1 not-first:mt-1", className)}
      {...props}
    />
  );
}

function SelectValue({ className, ...props }: SelectPrimitive.Value.Props) {
  return (
    <SelectPrimitive.Value
      data-slot="select-value"
      className={cn("flex flex-1 text-left", className)}
      {...props}
    />
  );
}

function SelectTrigger({
  className,
  size = "default",
  variant = "default",
  children,
  ...props
}: SelectPrimitive.Trigger.Props & {
  /** `xs` (22px) fits a select inline with a label, e.g. a card or panel title. */
  size?: "xs" | "compact" | "sm" | "default" | "lg";
  /** `ghost` drops the control chrome and keeps the chevron faint until hover or focus. */
  variant?: "default" | "ghost";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      data-variant={variant}
      className={cn(
        selectTriggerClassName,

        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon
        data-slot="select-icon"
        render={<IconChevronDown className="pointer-events-none size-4 text-muted-foreground" />}
      />
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  size = "default",
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  alignItemWithTrigger = false,
  ...props
}: SelectPrimitive.Popup.Props &
  Pick<
    SelectPrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset" | "alignItemWithTrigger"
  > & {
    /** `compact` scales items and labels down to match `xs` / `compact` triggers. */
    size?: "default" | "compact";
  }) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        alignItemWithTrigger={alignItemWithTrigger}
        className="isolate z-50"
      >
        <SelectPrimitive.Popup
          data-slot="select-content"
          data-size={size}
          data-align-trigger={alignItemWithTrigger}
          className={cn(
            "group/select-content popup-motion relative isolate z-50 max-h-(--available-height) w-(--anchor-width) min-w-36 overflow-x-hidden overflow-y-auto rounded-[var(--paper-radius-composite)] bg-popover p-1 text-xs text-popover-foreground shadow-[var(--paper-flyout-shadow)]",
            className,
          )}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.List>{children}</SelectPrimitive.List>
          <SelectScrollDownButton />
        </SelectPrimitive.Popup>
      </SelectPrimitive.Positioner>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: SelectPrimitive.GroupLabel.Props) {
  return (
    <SelectPrimitive.GroupLabel
      data-slot="select-label"
      // Section headers use small caps so they never read as a (disabled) option.
      className={cn(
        "px-2 pt-2 pb-1 text-[10px] leading-3 font-semibold tracking-caps text-muted-foreground uppercase",
        className,
      )}
      {...props}
    />
  );
}

function SelectItem({ className, children, ...props }: SelectPrimitive.Item.Props) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex min-h-8 w-full cursor-default items-center gap-2 rounded-md py-1.5 pr-8 pl-2.5 text-xs outline-hidden select-none transition-colors duration-[var(--duration-fast)] data-highlighted:bg-accent data-highlighted:text-accent-foreground data-selected:bg-control-highlight data-selected:font-medium active:bg-border not-data-[variant=destructive]:data-highlighted:**:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 group-data-[size=compact]/select-content:min-h-6 group-data-[size=compact]/select-content:rounded-xs group-data-[size=compact]/select-content:py-0.5 group-data-[size=compact]/select-content:pr-7 group-data-[size=compact]/select-content:pl-2 group-data-[size=compact]/select-content:text-[11px] group-data-[size=compact]/select-content:leading-[15px] group-data-[size=compact]/select-content:[&_svg:not([class*='size-'])]:size-3.5",
        className,
      )}
      {...props}
    >
      <SelectPrimitive.ItemText className="flex flex-1 shrink-0 gap-2 whitespace-nowrap">
        {children}
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator
        render={
          <span className="pointer-events-none absolute right-2 flex size-4 items-center justify-center group-data-[size=compact]/select-content:right-1.5 group-data-[size=compact]/select-content:size-3.5" />
        }
      >
        <IconCheck className="pointer-events-none" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({ className, ...props }: SelectPrimitive.Separator.Props) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpArrow>) {
  return (
    <SelectPrimitive.ScrollUpArrow
      data-slot="select-scroll-up-button"
      className={cn(
        "top-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <IconChevronUp />
    </SelectPrimitive.ScrollUpArrow>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownArrow>) {
  return (
    <SelectPrimitive.ScrollDownArrow
      data-slot="select-scroll-down-button"
      className={cn(
        "bottom-0 z-10 flex w-full cursor-default items-center justify-center bg-popover py-1 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    >
      <IconChevronDown />
    </SelectPrimitive.ScrollDownArrow>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  selectTriggerClassName,
};
